import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import { useAutosave, AutosaveStatus } from "@/lib/useAutosave";

/** Cost code x cost type checkbox matrix. Deactivated pairs can't be used on a line. */
export default function BudgetCodeMatrix() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState({});
  const [filter, setFilter] = useState("");
  const [editor, setEditor] = useState(null);   // {kind:'code'|'type', id?, code, name, active}
  // No Save button — a toggle saves itself, batched so a bulk column toggle is one call.
  const { status: saveStatus, trigger } = useAutosave(() => save(), { delay: 900 });

  const load = async () => {
    setData(await estApi.getMatrix());
    setPending({});
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const isActive = (display) =>
    pending[display] !== undefined ? pending[display] : (data?.active?.[display] ?? true);

  const toggle = (display) => {
    const next = !isActive(display);
    setPending((p) => {
      const copy = { ...p };
      if ((data?.active?.[display] ?? true) === next) delete copy[display];
      else copy[display] = next;
      return copy;
    });
    trigger();
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();
    return data.cost_codes.filter(
      (c) => !f || c.code.toLowerCase().includes(f) || (c.name || "").toLowerCase().includes(f)
    );
  }, [data, filter]);

  const save = async () => {
    const updates = Object.entries(pending).map(([display, active]) => ({ display, active }));
    if (!updates.length) return;
    try {
      await estApi.saveMatrix(updates);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const toggleColumn = (typeCode, active) => {
    setPending((p) => {
      const copy = { ...p };
      rows.forEach((c) => {
        const display = `${c.code}.${typeCode}`;
        if ((data.active[display] ?? true) === active) delete copy[display];
        else copy[display] = active;
      });
      return copy;
    });
  };

  // Adding a code or a type regenerates the pairs, so it reaches estimate lines,
  // the Cost Library and proposals straight away.
  const submitEditor = async (override = {}) => {
    const e0 = { ...editor, ...override };
    const isCode = e0.kind === "code";
    const code = (e0.code || "").trim();
    const name = (e0.name || "").trim();
    if (!e0.id && !code) return toast.error("A code is required");
    try {
      if (e0.id) {
        const body = { name, active: e0.active !== false };
        const r = isCode ? await estApi.updateCostCode(e0.id, body)
                         : await estApi.updateCostType(e0.id, body);
        toast.success(body.active ? `${e0.code} updated`
                                  : `${e0.code} retired — its pairs are switched off`);
        if (r.budget_codes_orphaned) toast.message(`${r.budget_codes_orphaned} pairs deactivated`);
      } else {
        const r = isCode ? await estApi.createCostCode({ code, name })
                         : await estApi.createCostType({ code, name });
        toast.success(isCode
          ? (r.postable ? `${code} added · ${r.budget_codes_created} pairs created`
                        : `${code} added as a division header — no pairs, it isn't postable`)
          : `${code} added · ${r.budget_codes_created} pairs created`);
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
    }
  };

  if (!data) {
    return <div className="p-6 text-sm text-slate-500" data-testid="matrix-loading">
      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading budget codes…
    </div>;
  }

  const pendingCount = Object.keys(pending).length;

  return (
    <div data-testid="budget-code-matrix">
      <div className="flex items-center gap-2 mb-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search..."
          className="h-8 w-56 rounded-md border border-slate-200 px-2 text-xs"
          data-testid="matrix-filter"
        />
        <span className="text-xs text-slate-500">
          {rows.length} postable codes × {data.cost_types.length} cost types
          {filter.trim() && <span className="text-amber-600"> · bulk toggles apply to filtered rows only</span>}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline"
                  onClick={() => setEditor({ kind: "code", code: "", name: "" })}
                  data-testid="matrix-add-cost-code-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Cost code
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setEditor({ kind: "type", code: "", name: "" })}
                  data-testid="matrix-add-cost-type-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Cost type
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const r = await estApi.regenerateBudgetCodes();
            toast.success(`${r.budget_codes_created} created, ${r.budget_codes_orphaned} orphaned`);
            load();
          }} data-testid="matrix-regenerate-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
          </Button>
          <AutosaveStatus status={saveStatus} />
        </div>
      </div>

      <div className="max-h-[520px] overflow-auto rounded-md border border-slate-200">
        <table className="text-xs border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 px-2 py-2 text-left font-medium text-slate-500 min-w-[240px]">
                Cost code
              </th>
              {data.cost_types.map((t) => (
                <th key={t.id} className="px-2 py-1 text-center font-medium text-slate-600 w-[52px]">
                  <button title={`${t.name} — rename or retire`}
                          className="hover:text-blue-600"
                          onClick={() => setEditor({ kind: "type", id: t.id, code: t.code,
                                                     name: t.name, active: true })}
                          data-testid={`matrix-edit-type-${t.code}`}>
                    {t.code}
                  </button>
                  <div className="flex justify-center gap-1 mt-0.5">
                    <button className="text-[9px] text-slate-400 hover:text-blue-600"
                            onClick={() => toggleColumn(t.code, true)}
                            data-testid={`matrix-col-all-${t.code}`}>all</button>
                    <button className="text-[9px] text-slate-400 hover:text-red-500"
                            onClick={() => toggleColumn(t.code, false)}
                            data-testid={`matrix-col-none-${t.code}`}>none</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="sticky left-0 bg-white px-2 py-1 whitespace-nowrap">
                  <span className="font-mono text-slate-800">{c.code}</span>
                  <span className="ml-2 text-slate-400 truncate">{c.name}</span>
                  <button className="ml-1.5 align-middle text-slate-300 hover:text-blue-600"
                          onClick={() => setEditor({ kind: "code", id: c.id, code: c.code,
                                                     name: c.name, active: true })}
                          data-testid={`matrix-edit-code-${c.code}`}>
                    <Pencil className="h-3 w-3" />
                  </button>
                </td>
                {data.cost_types.map((t) => {
                  const display = `${c.code}.${t.code}`;
                  const active = isActive(display);
                  const dirty = pending[display] !== undefined;
                  return (
                    <td key={t.id} className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggle(display)}
                        className={`h-3.5 w-3.5 cursor-pointer ${dirty ? "ring-2 ring-blue-400 rounded" : ""}`}
                        title={display}
                        data-testid={`matrix-cell-${display}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => setEditor(null)}>
          <div className="w-full max-w-sm rounded-lg bg-white shadow-xl" role="dialog"
               aria-modal="true" aria-label={editor.id ? `Edit ${editor.code}` : "Add a code"}
               onClick={(e) => e.stopPropagation()} data-testid="matrix-code-dialog">
            <header className="border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                {editor.id ? `Edit ${editor.code}`
                           : editor.kind === "code" ? "Add a cost code" : "Add a cost type"}
              </h3>
              <p className="mt-0.5 text-[11px] text-slate-500">
                {editor.kind === "code"
                  ? "A code with a dash (09-100) is postable and gets a full row of type pairs, all switched on. A bare division number (09) is stored as a header — no pairs, not postable."
                  : "A new cost type becomes a column, switched on for every postable code. Untick the pairs ABS doesn't use."}
              </p>
            </header>
            <div className="space-y-3 p-4">
              <label className="block">
                <span className="text-[11px] text-slate-500">Code</span>
                <input value={editor.code || ""} disabled={!!editor.id}
                       onChange={(e) => setEditor({ ...editor, code: e.target.value })}
                       placeholder={editor.kind === "code" ? "09-100" : "M"}
                       className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 font-mono text-xs disabled:bg-slate-50 disabled:text-slate-500"
                       data-testid="matrix-code-input" />
              </label>
              <label className="block">
                <span className="text-[11px] text-slate-500">Name</span>
                <input value={editor.name || ""}
                       onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                       placeholder={editor.kind === "code" ? "Toilet accessories" : "Material"}
                       className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs"
                       data-testid="matrix-name-input" />
              </label>
            </div>
            <footer className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
              {editor.id && (
                <Button size="sm" variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => submitEditor({ active: false })}
                        data-testid="matrix-retire-btn">
                  Retire
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditor(null)}
                        data-testid="matrix-code-cancel">Cancel</Button>
                <Button size="sm" onClick={() => submitEditor()} data-testid="matrix-code-save">
                  {editor.id ? "Save" : "Add"}
                </Button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
